import type { DrawingEntity, QualityIssue, Matrix3, TextEntity } from '../contract/types.js';
import type { Ledger } from './ledger.js';
import type { StyleContext } from './recognise.js';
import { applyTo, multiply, normaliseDegrees } from '../transform/matrix.js';
import { scaleFactorOf } from '../transform/space.js';

/**
 * Text extraction and reassembly.
 *
 * A PDF has no notion of a text *entity*. It has show-text operators, and one
 * visible line of a drawing arrives as however many runs the producer happened
 * to emit — split by font changes, by kerning, by per-glyph positioning. On a
 * Japanese drawing this is worse than usual, because switching between a CJK
 * font and an ASCII font fragments every mixed line: `図番 A-001` arrives as
 * three separate runs.
 *
 * There is also no MTEXT in a PDF. A multi-line block exported from CAD arrives
 * as one run per line, with nothing marking them as belonging together, and CAD
 * exports are not tagged PDFs so there is no structure tree to consult either.
 *
 * So producing TEXT and MTEXT entities requires a **reassembly step** — the same
 * class of problem as recognising a circle from four Bezier curves. The rules
 * below are that step. They live here, versioned with the extraction profile,
 * because two implementations that both read the PDF perfectly will score
 * differently against an exact-match accuracy gate if they join runs
 * differently. See docs/findings/06-text-has-no-entities.md.
 */

/** Two runs are on the same line if their baselines are within this fraction of the text height. */
export const SAME_LINE_TOLERANCE = 0.35;

/**
 * A horizontal gap up to this fraction of the text height is a join, not a break.
 *
 * Set above 1.0 deliberately: in a CJK font a single space is a full em, so a
 * legitimate space inside `図番 A-001` measures a whole text height. A tighter
 * threshold splits every mixed Japanese and ASCII line.
 */
export const JOIN_GAP_RATIO = 1.2;

/** Left edges within this fraction of the text height count as aligned. */
export const ALIGNMENT_TOLERANCE = 0.35;

/** Baseline spacing in this range, as a multiple of text height, is one block. */
export const BLOCK_SPACING_MIN = 1.0;
export const BLOCK_SPACING_MAX = 1.7;

/**
 * Spacing beyond BLOCK_SPACING_MAX but below this is a *marginal* case: close
 * enough that a different implementation would plausibly group it. These are
 * reported rather than silently decided, because a forced choice between two
 * equally supported readings is a confident wrong answer.
 */
export const BLOCK_SPACING_MARGINAL = 2.5;

interface RawItem {
  str?: string;
  transform?: number[];
  width?: number;
  height?: number;
  /** pdf.js's own handle for the font this run was set in, keying `styles`. */
  fontName?: string;
}

/**
 * The three families pdf.js resolves a PDF font to, and the only three read.
 *
 * Anything else is a pdf.js that changed its vocabulary, and picking whichever of
 * the three it probably meant would draw a run in the wrong metrics -- which is
 * the whole defect `fontFamily` exists to close. An unrecognised value is
 * therefore no value.
 */
const KNOWN_FONT_FAMILIES = ['serif', 'sans-serif', 'monospace'] as const;

type KnownFontFamily = (typeof KNOWN_FONT_FAMILIES)[number];

function classifyFamily(raw: unknown): KnownFontFamily | null {
  return typeof raw === 'string' && (KNOWN_FONT_FAMILIES as readonly string[]).includes(raw)
    ? (raw as KnownFontFamily)
    : null;
}

interface TextSource {
  getTextContent(options?: { includeMarkedContent?: boolean }): Promise<{
    items: unknown[];
    /** Per-font style records, keyed by the `fontName` each item carries. */
    styles?: Record<string, { fontFamily?: unknown }>;
  }>;
}

/**
 * One run as read from the PDF, already in page space.
 *
 * `along` and `across` are the run's position projected onto its **own reading
 * frame**: `along` runs in the direction the text advances, `across` in the
 * direction successive lines stack. Comparing runs on page axes instead works
 * only for upright text — on a rotated page, or for rotated text on an upright
 * page, "the next line down" is not down the page, and every line ends up its
 * own entity.
 */
export interface Run {
  text: string;
  x: number;
  y: number;
  along: number;
  across: number;
  width: number;
  height: number;
  rotation: number;
  /** The family pdf.js resolved this run's font to, where it classified one. */
  fontFamily: KnownFontFamily | null;
  /**
   * Where the producer wrote this in the content stream, counting only runs that
   * carry characters. `seq` is the first such position the run covers and
   * `seqEnd` the last; they differ once runs have been joined into a line.
   *
   * Carried because the order a producer writes text in is the only evidence of
   * where one entity ends and the next begins. See `writtenConsecutively`.
   */
  seq: number;
  seqEnd: number;
}

/** Projects a page-space point onto the reading frame of text at this rotation. */
export function project(x: number, y: number, rotationDeg: number): { along: number; across: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Page space measures y downward, so the advance direction of text rotated
  // counter-clockwise by theta is (cos, -sin), and lines stack along (sin, cos).
  return { along: x * cos - y * sin, across: x * sin + y * cos };
}

export interface TextResult {
  entities: DrawingEntity[];
  issues: QualityIssue[];
}

export async function extractTextEntities(
  page: TextSource,
  pageIndex: number,
  base: Matrix3,
  style: StyleContext,
  ledger: Ledger,
): Promise<TextResult> {
  const content = await page.getTextContent({ includeMarkedContent: false });
  const runs: Run[] = [];

  for (const raw of content.items) {
    const item = raw as RawItem;
    const str = item.str;
    if (typeof str !== 'string' || str.length === 0) continue;

    const t = (item.transform ?? [1, 0, 0, 1, 0, 0]) as unknown as Matrix3;
    const world = multiply(base, t);
    const anchor = applyTo(world, { x: 0, y: 0 });
    const k = scaleFactorOf(world);

    ledger.consume('showText');

    // pdf.js emits synthetic whitespace runs whose *width encodes the gap*
    // between two pieces of real text — a run of `" "` 68 points wide sitting
    // between two title-block fields. They report a height of zero, which is
    // the tell. Treated as content they make every field on a line contiguous,
    // and the whole line joins into one. They carry no characters, so they are
    // dropped here and the real geometric gap decides instead. The ledger has
    // already counted them: they are consumed into the spacing of the line.
    if (/^\s*$/.test(str)) continue;

    const rotation = normaliseDegrees(-(Math.atan2(world[1], world[0]) * 180) / Math.PI);
    const { along, across } = project(anchor.x, anchor.y, rotation);

    runs.push({
      text: str,
      x: anchor.x,
      y: anchor.y,
      along,
      across,
      // Counting only the runs kept, so the blank spacing runs dropped above
      // cannot make two consecutive pieces of text look separated.
      seq: runs.length,
      seqEnd: runs.length,
      // The item's width is in text space; scale it into page space so gaps
      // between runs are measured in the same units as the text height.
      width: (item.width ?? 0) * (k / Math.max(Math.hypot(t[0], t[1]), 1e-9)),
      height: Math.abs(item.height ?? 0) || Math.hypot(t[2], t[3]) || 2.5,
      rotation,
      // From pdf.js's own classification of the face, which is what its text
      // layer uses to make a run register with the page beneath it.
      fontFamily: classifyFamily(
        item.fontName === undefined ? undefined : content.styles?.[item.fontName]?.fontFamily,
      ),
    });
  }

  const lines = joinRunsIntoLines(runs);
  const { blocks, issues } = groupLinesIntoBlocks(lines, pageIndex);

  const entities: DrawingEntity[] = blocks.map((block) => {
    const first = block[0]!;
    const pitch = blockPitch(block);
    const entity: TextEntity = {
      id: style.ids.next(),
      type: block.length > 1 ? 'MTEXT' : 'TEXT',
      text: block.map((l) => l.text).join('\n'),
      anchor: { x: first.x, y: first.y },
      height: first.height,
      rotation: first.rotation,
      // Kept only where there is more than one line: for a single line the
      // block's own bbox already describes it, and `advanceOf` uses that.
      ...(block.length > 1 ? { lineWidths: block.map((l) => l.width) } : {}),
      // Measured where the lines stack, so it survives a rotated page. See
      // `blockPitch`.
      ...(pitch !== null ? { linePitch: pitch } : {}),
      // The first line's face speaks for the block: a run is only joined to it if
      // it shares a baseline and a height, and reassembly does not mix families
      // across lines in any drawing this has seen.
      ...(first.fontFamily !== null ? { fontFamily: first.fontFamily } : {}),
      layer: style.layer,
      strokeWidth: style.strokeWidth,
      colour: style.colour,
      source: {
        pageIndex,
        operatorIndex: -1,
        bbox: blockBox(block),
      },
      origin: 'vector',
    };
    return entity;
  });

  return { entities, issues };
}

/**
 * Joins runs that sit on the same baseline into one line.
 *
 * Without this, a mixed Japanese and ASCII line scores as one false negative
 * and three false positives against an exact-match gate, on an extraction that
 * read every character correctly.
 */
export function joinRunsIntoLines(runs: readonly Run[]): Run[] {
  // Runs are banded onto baselines first, and only then ordered along the line.
  //
  // Sorting by `across` and falling back to `along` looks equivalent and is not.
  // The projection multiplies a coordinate by cos(270°), which is not exactly
  // zero in floating point, so two runs sharing a baseline differ in `across`
  // by a few times 1e-15 — enough for the primary key to decide, and to order
  // the second half of a line *before* its first half. The gap test then sees a
  // large negative gap, refuses the join, and one line becomes two entities. On
  // an upright page the term vanishes exactly, which is why this only appears
  // on a rotated page, and only on lines the producer split into runs.
  const bands: Run[][] = [];
  for (const run of [...runs].sort((a, b) => a.across - b.across)) {
    const band = bands[bands.length - 1];
    const reference = band?.[0];
    const sameBand =
      reference !== undefined &&
      Math.abs(run.across - reference.across) <=
        Math.max(reference.height, run.height) * SAME_LINE_TOLERANCE;
    if (band && sameBand) band.push(run);
    else bands.push([run]);
  }

  const lines: Run[] = [];
  for (const band of bands) {
    band.sort((a, b) => a.along - b.along);
    for (const run of band) {
      const last = lines[lines.length - 1];
      if (last && canJoin(last, run)) {
        const gap = run.along - (last.along + last.width);
        // A visible gap is a space; an adjacent run continues the same word.
        const separator = gap > last.height * 0.18 && !/\s$/.test(last.text) ? ' ' : '';
        last.text = `${last.text}${separator}${run.text}`;
        last.width = run.along + run.width - last.along;
        last.height = Math.max(last.height, run.height);
        // A joined line spans every position its runs came from, so the next
        // line is adjacent to the last of them rather than to the first.
        last.seq = Math.min(last.seq, run.seq);
        last.seqEnd = Math.max(last.seqEnd, run.seqEnd);
      } else {
        lines.push({ ...run });
      }
    }
  }

  for (const line of lines) line.text = line.text.replace(/\s+/g, ' ').trim();
  return lines.filter((l) => l.text.length > 0);
}

function canJoin(a: Run, b: Run): boolean {
  if (angleGap(a.rotation, b.rotation) > 0.5) return false;
  const h = Math.max(a.height, b.height);
  if (Math.abs(a.across - b.across) > h * SAME_LINE_TOLERANCE) return false;
  // Heights may differ a little across a font switch, but not a lot.
  if (Math.abs(a.height - b.height) > h * 0.35) return false;
  const gap = b.along - (a.along + a.width);
  return gap >= -h * 0.5 && gap <= h * JOIN_GAP_RATIO;
}

/** Smallest absolute difference between two angles in degrees. */
function angleGap(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360 + 360) % 360);
  return Math.min(d, 360 - d);
}

/**
 * Groups consecutive lines into multi-line blocks.
 *
 * Marginal spacing is reported rather than decided. Where two lines are close
 * enough that a different implementation would plausibly group them, the pair
 * is recorded as a merge candidate so the decision is visible instead of buried
 * in a headline accuracy figure.
 */
export function groupLinesIntoBlocks(
  lines: readonly Run[],
  pageIndex: number,
): { blocks: Run[][]; issues: QualityIssue[] } {
  const blocks: Run[][] = [];
  const issues: QualityIssue[] = [];
  const sorted = [...lines].sort((a, b) => a.along - b.along || a.across - b.across);
  const used = new Set<number>();

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const block = [sorted[i]!];
    used.add(i);

    let current = sorted[i]!;
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;
      const next = sorted[j]!;
      const verdict = spacingVerdict(current, next);
      const consecutive = writtenConsecutively(current, next);
      const spacing = (Math.abs(next.y - current.y) / current.height).toFixed(2);

      if (verdict === 'grouped' && consecutive) {
        block.push(next);
        used.add(j);
        current = next;
      } else if (verdict === 'marginal' || verdict === 'grouped') {
        // Both remaining cases are ones where another implementation would
        // plausibly have grouped, so both are reported rather than buried: the
        // gate scores the reassembly, and a decision it cannot see is a decision
        // nobody can check.
        issues.push({
          kind: 'merge_candidate',
          count: 1,
          pageIndex,
          bbox: blockBox([current, next]),
          detail: consecutive
            ? `line spacing of ${spacing} text heights is between the grouped and ` +
              'separate thresholds; kept separate, and reported because another ' +
              'reading is defensible'
            : `line spacing of ${spacing} text heights would group these, but the ` +
              `producer wrote them ${Math.abs(next.seq - current.seqEnd)} runs apart ` +
              'in the content stream; kept separate, and reported because another ' +
              'reading is defensible',
        });
      }
    }
    blocks.push(block);
  }

  return { blocks, issues };
}

/**
 * True when the producer wrote these two lines next to each other.
 *
 * A producer writes a multi-line entity as a unit: every block in the generated
 * fixtures and in the sample drawings emits its lines consecutively. A table is
 * written across its rows instead, so a column's neighbours are separated by a
 * whole row of other cells.
 *
 * That ordering is the **only** evidence separating ten stacked single-line
 * entities from one ten-line block. Measured on a sample drawing, a column of
 * labels sits at 1.33 text heights and two lines of one note at 1.19 -- the
 * independent pair further apart than the joined one, at identical left edge,
 * height and rotation. No geometric threshold can divide those; the order they
 * were written in divides them cleanly, nineteen runs against one.
 *
 * Either direction counts, because a producer may write a block bottom-up while
 * the drawing's reading order runs the other way.
 *
 * This narrows grouping and never widens it: a pair that fails here was going to
 * be one entity and becomes two, so the rule can only split blocks that used to
 * merge, never merge lines that used to stay apart. The cost is a producer that
 * interleaves one block's lines with other text, which would now under-merge --
 * the safer direction, since an under-merged block still carries every string it
 * read, while an over-merged one destroys both entities' text.
 */
function writtenConsecutively(a: Run, b: Run): boolean {
  return b.seq === a.seqEnd + 1 || a.seq === b.seqEnd + 1;
}

function spacingVerdict(a: Run, b: Run): 'grouped' | 'marginal' | 'separate' {
  if (angleGap(a.rotation, b.rotation) > 0.5) return 'separate';
  const h = Math.max(a.height, b.height);
  if (Math.abs(a.height - b.height) > h * 0.05) return 'separate';
  // Left edges are compared along the reading direction, not the page x axis.
  if (Math.abs(a.along - b.along) > h * ALIGNMENT_TOLERANCE) return 'separate';

  // Lines stack in the `across` direction of the text's own frame.
  const gap = (b.across - a.across) / h;
  if (gap >= BLOCK_SPACING_MIN && gap <= BLOCK_SPACING_MAX) return 'grouped';
  if (gap > BLOCK_SPACING_MAX && gap <= BLOCK_SPACING_MARGINAL) return 'marginal';
  return 'separate';
}

/**
 * The baseline pitch inside a block, measured where the lines actually stack.
 *
 * `across` is the run's position along the direction lines descend in, which
 * `project` derives from the run's own rotation -- so this is the same number for
 * an upright page and a rotated one.
 *
 * Carried on the entity rather than recovered later, for the same reason
 * `lineWidths` is. The pitch used to be got back by inverting `source.bbox`:
 * `(|y1 - y0| - height) / (lines - 1)`. That box is a union in PAGE axes while a
 * run's width and height are along its OWN, so on a rotated page the line
 * stacking landed in the box's x extent and its y extent collapsed to one line
 * height. The inversion returned 0, fell through to a constant, and every note on
 * the four rotated fixtures was drawn 14 % tight -- in the overlay and in DXF
 * group 44 -- with no gate able to see it, because no truth file declares a pitch.
 *
 * Averaged over the block rather than taken from the first gap, which costs
 * nothing and is right for a producer whose leading varies by a rounding.
 */
export function blockPitch(block: readonly Run[]): number | null {
  if (block.length < 2) return null;
  const first = block[0]!;
  const last = block[block.length - 1]!;
  return (last.across - first.across) / (block.length - 1);
}

function blockBox(block: readonly Run[]): readonly [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const l of block) {
    minX = Math.min(minX, l.x);
    minY = Math.min(minY, l.y - l.height);
    maxX = Math.max(maxX, l.x + l.width);
    maxY = Math.max(maxY, l.y);
  }
  return [minX, minY, maxX, maxY];
}

/**
 * Detects text stored as outlines — PRD Finding 5.
 *
 * Drawings produced with CAD stroke fonts, long the default for Japanese
 * mechanical drawings, export to PDF as line-work rather than characters. There
 * is nothing to extract: no text, no font, no string. Text recall on such a
 * drawing is not low, it is zero, and no amount of tuning changes it.
 *
 * The remedy is optical recognition, which is later-roadmap work. What belongs
 * here is *reporting* it, so the system says "this drawing contains text I
 * cannot read" rather than silently scoring zero.
 *
 * The heuristic is deliberately conservative: many small closed paths on a page
 * with little extractable text. A false negative leaves the reader no worse off;
 * a false positive would cry wolf on ordinary line-work.
 */
export const OUTLINED_TEXT_MIN_PATHS = 20;

/**
 * How many times more glyph-shaped paths than readable text spans before the
 * page is called suspect. A drawing with outlined text usually still has *some*
 * readable text — a stamp, a revision block, a note added later — so requiring
 * near-zero readable text misses the real cases.
 */
export const OUTLINED_TEXT_PATH_TO_TEXT_RATIO = 2;

export function detectOutlinedText(
  smallClosedPathBoxes: readonly (readonly [number, number, number, number])[],
  extractedTextCount: number,
  pageIndex: number,
): QualityIssue | null {
  const n = smallClosedPathBoxes.length;
  if (n < OUTLINED_TEXT_MIN_PATHS) return null;
  if (n <= extractedTextCount * OUTLINED_TEXT_PATH_TO_TEXT_RATIO) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x0, y0, x1, y1] of smallClosedPathBoxes) {
    minX = Math.min(minX, x0);
    minY = Math.min(minY, y0);
    maxX = Math.max(maxX, x1);
    maxY = Math.max(maxY, y1);
  }

  return {
    kind: 'outlined_text',
    count: n,
    pageIndex,
    bbox: [minX, minY, maxX, maxY],
    detail:
      `${n} small closed paths against ${extractedTextCount} extractable text spans: ` +
      'this drawing probably carries text drawn as outlines, which cannot be read as characters',
  };
}
